import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE, resolveImageUrl } from "../../config/api";
import Pagination from "../../Components/Pagination";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";

const PAGE_SIZE = 25;

interface Shop {
  shop_id: number;
  store_id: number;
  shop_name: string;
  shop_description: string;
  shop_image: string;
  shop_image_url?: string;
  address?: { city?: string } | null;
  created_at: string;
  updated_at: string;
}

interface EditShop {
  shop_id?: number;
  store_id?: number;
  shop_name: string;
  shop_description: string;
  shop_image: string;
  shop_image_url?: string;
  city: string;
}

interface Store {
  store_id: number;
  store_name: string;
}

const ShopsPage = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedShops, setSelectedShops] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editShop, setEditShop] = useState<EditShop>({
    shop_name: "",
    shop_description: "",
    shop_image: "",
    city: ""
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchShops = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/shop?${params.toString()}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setShops(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch shops. Status:", response.status);
        console.error("Error response:", errorText);
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/store?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setStores(Array.isArray(data?.data) ? data.data : []);
      } else {
        console.error("Failed to fetch stores. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [cookies.auth]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleAdd = () => {
    const userType = cookies.auth?.user_type;
    // store_id on the session is an array (an Admin may own more than one store) — default to the first.
    const ownedStoreIds: number[] = cookies.auth?.store_id || [];
    const defaultStoreId = (userType === 'A' || userType === 'S') ? ownedStoreIds[0] : undefined;
    setEditShop({
      shop_name: "",
      shop_description: "",
      shop_image: "",
      store_id: defaultStoreId,
      city: "",
    });
    setImageFile(null);
    resetDropdowns();
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedShops.length !== 1) {
      alert("Please select exactly one shop to update");
      return;
    }
    
    const shopToEdit = shops.find(shop => shop.shop_id === selectedShops[0]);
    if (shopToEdit) {
      setEditShop({
        shop_id: shopToEdit.shop_id,
        store_id: shopToEdit.store_id,
        shop_name: shopToEdit.shop_name,
        shop_description: shopToEdit.shop_description,
        shop_image: shopToEdit.shop_image || "",
        shop_image_url: shopToEdit.shop_image_url,
        city: shopToEdit.address?.city || "",
      });
      setImageFile(null);
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedShops.length === 0) {
      alert("Please select at least one shop to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const shopId of selectedShops) {
        const response = await authenticatedFetch(`${API_BASE}/shop/${shopId}`, {
          method: "DELETE"
        }, cookies.auth);
        if (!response.ok) {
          throw new Error(`Failed to delete shop ${shopId}: ${response.status}`);
        }
      }
      setSelectedShops([]);
      setShowDeleteConfirm(false);
      await fetchShops();
    } catch (error) {
      console.error("Error deleting shops:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);
      let imageBase64: string | null = null;
      
      if (imageFile) {
        try {
          imageBase64 = await convertFileToBase64(imageFile!);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          return;
        }
      }

      // Validate required fields
      if (!editShop.shop_name.trim()) {
        snack.onResponse({ message: "Shop name is required", status: 400 });
        return;
      }
      if (!editShop.store_id || editShop.store_id === undefined) {
        snack.onResponse({ message: "Please select a store", status: 400 });
        return;
      }

      const storeId = parseInt(editShop.store_id.toString());
      if (isNaN(storeId)) {
        snack.onResponse({ message: "Invalid store ID", status: 400 });
        return;
      }

      const payload: Record<string, unknown> = {
        store_id: storeId,
        shop_name: editShop.shop_name.trim(),
        shop_description: editShop.shop_description.trim(),
        shop_image: imageBase64 || editShop.shop_image,
      };
      if (editShop.city.trim()) {
        payload.address = { city: editShop.city.trim() };
      }

      console.log("=== SAVE SHOP DEBUG ===");
      console.log("Is Edit:", isEdit);
      console.log("Payload:", payload);
      console.log("Edit Shop:", editShop);

      const url = isEdit 
        ? `${API_BASE}/shop/${editShop.shop_id}`
        : `${API_BASE}/shop`;
      
      const method = isEdit ? "PUT" : "POST";
      
      console.log("URL:", url);
      console.log("Method:", method);

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload),
      }, cookies.auth);

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (!response.ok) {
        snack.onResponse({ message: await parseApiError(response, "Failed to save shop"), status: response.status });
        return;
      }

      const responseData = await response.json();
      console.log("Success response:", responseData);
      console.log("=== END SAVE SHOP DEBUG ===");

      setShowEditModal(false);
      setShowAddModal(false);
      setEditShop({
        shop_name: "",
        shop_description: "",
        shop_image: "",
        store_id: undefined,
        city: "",
      });
      setImageFile(null);
      resetDropdowns();
      setSelectedShops([]); // Clear selection after successful update
      await fetchShops();
    } catch (error) {
      console.error("Error saving shop:", error);
      const message = error instanceof Error ? error.message : typeof error === "string" ? error : "Failed to save shop";
      snack.onResponse({ message, status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedShops(shops.map(shop => shop.shop_id));
    } else {
      setSelectedShops([]);
    }
  };

  const handleSelectShop = (shopId: number, checked: boolean) => {
    if (checked) {
      // Single selection for update - clear all others and select only this one
      setSelectedShops([shopId]);
    } else {
      setSelectedShops(selectedShops.filter(id => id !== shopId));
    }
  };

  const filteredShops = shops;

  const getStoreName = (storeId: number | null | undefined) => {
    if (!storeId) return "Unknown";
    const store = stores.find(s => s.store_id === storeId);
    return store ? store.store_name : "Unknown";
  };

  const resetDropdowns = () => {
    // Reset any dropdown states if needed
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <div className={style.headerContent}>
          <h1 className={style.title}>Shop</h1>
          <input
            type="text"
            placeholder="Search by shop name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>
                Add
              </button>
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete Shop
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedShops.length === shops.length && shops.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Shop ID</th>
              <th>Store</th>
              <th>Shop Name</th>
              <th>Description</th>
              <th>City</th>
              <th>Image</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filteredShops.map((shop) => (
              <tr key={shop.shop_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedShops.includes(shop.shop_id)}
                      onChange={(e) => handleSelectShop(shop.shop_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{shop.shop_id}</td>
                <td>{getStoreName(shop.store_id)}</td>
                <td>{shop.shop_name}</td>
                <td className={style.descriptionCell}>
                  <TruncatedText text={shop.shop_description} maxLength={50} />
                </td>
                <td>{shop.address?.city || '—'}</td>
                <td>
                  <img
                    src={shop.shop_image_url
                      ? resolveImageUrl(shop.shop_image_url)
                      : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzNUMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43NjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }
                    alt={shop.shop_name}
                    className={style.storeImage}
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzNUMyNSAzMy43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }}
                  />
                </td>
                <td>{formatDate(shop.created_at)}</td>
                <td>{formatDate(shop.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add New Shop" : "Update Shop"}</h2>
            <div className={style.modalContent}>
              <input
                type="text"
                placeholder="Shop Name *"
                value={editShop.shop_name}
                onChange={(e) => setEditShop({...editShop, shop_name: e.target.value})}
                className={style.modalInput}
                required
              />
              {cookies.auth?.user_type === 'O' ? (
                <select
                  value={editShop.store_id || ""}
                  onChange={(e) => {
                    const storeId = parseInt(e.target.value);
                    const store = stores.find(s => s.store_id === storeId);
                    if (store) {
                      setEditShop({...editShop, store_id: storeId});
                    }
                  }}
                  className={style.modalInput}
                  required
                >
                  <option value="">Select Store *</option>
                  {stores.map(store => (
                    <option key={store.store_id} value={store.store_id}>
                      {store.store_name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={getStoreName(editShop.store_id)}
                  readOnly
                  className={style.modalInput}
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              )}
              <textarea
                placeholder="Shop Description"
                value={editShop.shop_description}
                onChange={(e) => setEditShop({...editShop, shop_description: e.target.value})}
                className={style.modalInput}
              />
              <input
                type="text"
                placeholder="City *"
                value={editShop.city}
                onChange={(e) => setEditShop({...editShop, city: e.target.value})}
                className={style.modalInput}
                required
              />
              <input
                type="file"
                onChange={handleImageChange}
                className={style.modalInput}
                accept="image/*"
              />
              {editShop.shop_image && !imageFile && (
                <img
                  src={resolveImageUrl(editShop.shop_image_url)}
                  alt="Current shop"
                  className={style.currentImage}
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzNUMyNSAzMy43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                  }}
                />
              )}
              <div className={style.modalButtons}>
                <button
                  onClick={() => handleSave(showEditModal)}
                  className={style.saveBtn}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : (showAddModal ? "Add Shop" : "Update Shop")}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className={style.cancelBtn}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete {selectedShops.length} selected shop(s)?</p>
            <div className={style.modalButtons}>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={style.cancelBtn}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopsPage;